import { useEffect, useRef, useState } from 'react';
import { MoreVertical, Download, Gauge, Check } from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';

interface AudioPlayerProps {
  src: string;
  className?: string;
  /**
   * Optional filename hint for the Download menu item. Browsers honor the
   * `download` attribute on same-origin URLs to override Content-Disposition.
   * If omitted the server's filename (or the URL's last segment) is used.
   *
   * Tip: callers usually want something like `${refNumber}.wav` so the
   * downloaded file isn't called `audio` or a UUID. The download is a
   * convenience for portal users — it does NOT change the recording that
   * callers hear, only what gets saved to disk.
   */
  downloadName?: string;
  /**
   * Compact rendering for tight rows like the IVR tree. Shrinks the play
   * button, the 3-dot menu, and the time displays so the player slots into
   * a row of height ~28px without pushing the row taller. Default false
   * (full-size for detail pages, dialogs, etc.).
   */
  compact?: boolean;
}

/**
 * Speed presets surfaced in the 3-dot menu. These adjust ONLY the portal
 * <audio> element's playbackRate for one-time listening — they do not change
 * the underlying recording or anything callers hear on the phone.
 */
const SPEED_PRESETS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;
type SpeedPreset = typeof SPEED_PRESETS[number];

function formatTime(secs: number): string {
  if (!isFinite(secs) || isNaN(secs)) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function AudioPlayer({ src, className, downloadName, compact = false }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  /**
   * playbackRate persists across src changes — if the user picked 1.5x to
   * listen to one recording, the next one they open should also start at
   * 1.5x rather than silently resetting to 1x. Re-applied in the load-effect
   * below after el.load() recreates the audio element's internal state.
   */
  const [speed, setSpeed] = useState<SpeedPreset>(1);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    // Reset state whenever the source changes so stale play/time values from
    // the previous track don't bleed through before the new track loads.
    el.pause();
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setLoading(false);
    // Tell the audio element to pick up the new src immediately — without this
    // the element holds onto the previously-loaded resource even though the
    // src attribute has changed on the DOM node.
    el.load();
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => { setPlaying(false); setCurrentTime(0); };
    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onDurationChange = () => setDuration(el.duration);
    const onWaiting = () => setLoading(true);
    const onCanPlay = () => setLoading(false);

    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('timeupdate', onTimeUpdate);
    el.addEventListener('durationchange', onDurationChange);
    el.addEventListener('waiting', onWaiting);
    el.addEventListener('canplay', onCanPlay);

    return () => {
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('timeupdate', onTimeUpdate);
      el.removeEventListener('durationchange', onDurationChange);
      el.removeEventListener('waiting', onWaiting);
      el.removeEventListener('canplay', onCanPlay);
    };
  }, [src]);

  // Keep the <audio> element's playbackRate in sync with the user's chosen
  // speed — fires both on speed change AND after a src change (because
  // load() resets playbackRate to 1.0 on some browsers).
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.playbackRate = speed;
  }, [speed, src]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
    } else {
      el.play().catch(() => undefined);
    }
  };

  const seek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = audioRef.current;
    if (!el) return;
    const newTime = Number(e.target.value);
    el.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      className={cn(
        'flex items-center rounded-lg border border-slate-200 bg-slate-50',
        compact ? 'gap-1.5 px-2 py-0.5' : 'gap-3 px-4 py-2.5',
        className,
      )}
      role="region"
      aria-label="Audio player"
    >
      {/* Hidden native audio */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* Play / Pause button */}
      <button
        type="button"
        onClick={togglePlay}
        disabled={loading}
        aria-label={playing ? 'Pause' : 'Play'}
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full bg-blue-600 text-white',
          'transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
          'disabled:opacity-50',
          compact ? 'h-5 w-5' : 'h-8 w-8',
        )}
      >
        {loading ? (
          /* Minimal spinner */
          <span className={cn(
            'animate-spin rounded-full border-2 border-white border-t-transparent',
            compact ? 'h-2 w-2' : 'h-3 w-3',
          )} />
        ) : playing ? (
          /* Pause icon — two vertical bars */
          <svg viewBox="0 0 16 16" fill="currentColor" className={compact ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'}>
            <rect x="3" y="2" width="3.5" height="12" rx="1" />
            <rect x="9.5" y="2" width="3.5" height="12" rx="1" />
          </svg>
        ) : (
          /* Play icon — filled triangle */
          <svg viewBox="0 0 16 16" fill="currentColor" className={cn(
            'translate-x-px',
            compact ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5',
          )}>
            <path d="M5 3.5 13 8 5 12.5z" />
          </svg>
        )}
      </button>

      {/* Time display — current */}
      <span
        className={cn(
          'shrink-0 text-right font-mono tabular-nums text-slate-500',
          compact ? 'w-7 text-[10px]' : 'w-9 text-xs',
        )}
        aria-label="Current time"
      >
        {formatTime(currentTime)}
      </span>

      {/* Scrub track */}
      <div className="relative flex-1">
        {/* Filled bar (visual only, behind the range input) */}
        <div className="pointer-events-none absolute inset-y-0 flex items-center">
          <div className="h-1 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full rounded-full bg-blue-500 transition-[width] duration-75"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        {/* Range input — transparent track, keeps native accessibility */}
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={seek}
          aria-label="Seek"
          className={cn(
            'relative w-full cursor-pointer appearance-none bg-transparent',
            // Thumb styling via Tailwind arbitrary values
            '[&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3',
            '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600',
            '[&::-webkit-slider-thumb]:appearance-none',
            '[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white',
            '[&::-webkit-slider-thumb]:shadow-sm',
            '[&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3',
            '[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-600',
            '[&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white',
            // Hide the track so our custom bar shows through
            '[&::-webkit-slider-runnable-track]:opacity-0',
            '[&::-moz-range-track]:opacity-0',
            'h-4', // height must be enough for thumb tap target
          )}
        />
      </div>

      {/* Time display — total */}
      <span
        className={cn(
          'shrink-0 font-mono tabular-nums text-slate-400',
          compact ? 'w-7 text-[10px]' : 'w-9 text-xs',
        )}
        aria-label="Total duration"
      >
        {formatTime(duration)}
      </span>

      {/* 3-dot menu — Download + Speed.
          Speed adjusts ONLY the portal <audio> playbackRate; it does NOT
          alter the underlying recording or anything callers hear. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="More audio options"
            title="More options"
            className={cn(
              'flex shrink-0 items-center justify-center rounded-full text-slate-500',
              'hover:bg-slate-200/70 hover:text-slate-700',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
              compact ? 'h-5 w-5' : 'h-7 w-7',
            )}
            // The IVR tree row wraps players in stopPropagation, but other
            // surfaces don't — defending here keeps a click on the menu from
            // bubbling up to a parent <button>/<form> handler.
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className={compact ? 'h-3 w-3' : 'h-4 w-4'} />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-44">
          {/* Download — uses the native <a download> on the streaming URL. */}
          <DropdownMenuItem asChild>
            <a
              href={src}
              download={downloadName ?? ''}
              // Hint to screen readers; visually we still get the icon + label.
              aria-label={`Download${downloadName ? ` ${downloadName}` : ''}`}
              className="flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              <Download className="h-4 w-4" />
              <span>Download</span>
            </a>
          </DropdownMenuItem>

          {/* Speed submenu. Radix Sub renders to a portal so it works inside
              tooltips, dialogs, popovers, and indented tree rows alike. */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="flex items-center gap-2">
              <Gauge className="h-4 w-4" />
              <span>Speed</span>
              <span className="ml-auto text-xs text-slate-500 tabular-nums">
                {speed === 1 ? 'normal' : `${speed}x`}
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent className="w-32">
                {SPEED_PRESETS.map((rate) => {
                  const active = rate === speed;
                  return (
                    <DropdownMenuItem
                      key={rate}
                      onClick={() => setSpeed(rate)}
                      className="flex items-center gap-2"
                    >
                      <span className="w-4 inline-flex items-center justify-center">
                        {active && <Check className="h-3.5 w-3.5" />}
                      </span>
                      <span className="tabular-nums">
                        {rate === 1 ? '1x (normal)' : `${rate}x`}
                      </span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
